<script>
  export default {
    name: "MoleculeDatePicker",
    props: {
      title: {
        type: String,
        default: "Fecha",
      },
      placeholder: {
        type: String,
        default: "Seleccionar fecha",
      },
      dateFrom: {
        type: Date,
        default: null,
      },
      dateTo: {
        type: Date,
        default: null,
      },
    },
    emits: ["date-changed"],
    data() {
      return {
        options: {
          month: "short",
          day: "numeric",
          timeZone: "America/Bogota",
        },
        internalDateFrom: null,
        internalDateTo: null,
        lastClickInsidePicker: false,
        defaultPickerOutsideHandler: null,
      };
    },
    watch: {
      dateFrom: {
        handler(newVal) {
          this.internalDateFrom = newVal;
          if (newVal === null && this.$refs.datePicker) {
            this.$nextTick(() => {
              if (this.$refs.datePicker.startDate) {
                this.$refs.datePicker.startDate = null;
              }
              if (this.$refs.datePicker.endDate) {
                this.$refs.datePicker.endDate = null;
              }
            });
          }
        },
        immediate: true,
      },
      dateTo: {
        handler(newVal) {
          this.internalDateTo = newVal;
          if (newVal === null && this.$refs.datePicker) {
            this.$nextTick(() => {
              if (this.$refs.datePicker.startDate) {
                this.$refs.datePicker.startDate = null;
              }
              if (this.$refs.datePicker.endDate) {
                this.$refs.datePicker.endDate = null;
              }
            });
          }
        },
        immediate: true,
      },
    },
    methods: {
      openPicker() {
        if (this.hasVisiblePickerPanel()) return;
        this.togglePicker();
      },
      closePicker() {
        if (!this.hasVisiblePickerPanel()) return;
        this.togglePicker();
      },
      togglePicker() {
        if (!this.$refs.datePicker || !this.$refs.datePicker.togglePicker) return;

        this.$refs.datePicker.togglePicker();
      },
      hasVisiblePickerPanel() {
        const panels = Array.from(document.querySelectorAll(".mdrp__panel"));

        return panels.some((panel) => {
          if (!panel) return false;

          const style = window.getComputedStyle(panel);
          const isVisible = style.display !== "none" && style.visibility !== "hidden" && panel.getClientRects().length > 0;

          return isVisible;
        });
      },
      disablePickerOutsideClose() {
        const pickerRootEl = this.$refs.datePicker && this.$refs.datePicker.$el;
        if (!pickerRootEl || !pickerRootEl.__vueClickOutside__) return;

        this.defaultPickerOutsideHandler = pickerRootEl.__vueClickOutside__;
        document.removeEventListener("click", this.defaultPickerOutsideHandler, true);

        pickerRootEl.__vueClickOutside__ = () => false;
        document.addEventListener("click", pickerRootEl.__vueClickOutside__, true);
      },
      restorePickerOutsideClose() {
        const pickerRootEl = this.$refs.datePicker && this.$refs.datePicker.$el;
        if (!pickerRootEl || !pickerRootEl.__vueClickOutside__) return;

        document.removeEventListener("click", pickerRootEl.__vueClickOutside__, true);

        if (this.defaultPickerOutsideHandler) {
          pickerRootEl.__vueClickOutside__ = this.defaultPickerOutsideHandler;
          document.addEventListener("click", this.defaultPickerOutsideHandler, true);
          this.defaultPickerOutsideHandler = null;
        }
      },
      handleDateChange(values) {
        if (!this.lastClickInsidePicker) {
          this.lastClickInsidePicker = false;
          return;
        }

        if (values && values.length === 2 && values[0] && values[1]) {
          const dateFrom = values[0]._d;
          const dateTo = values[1]._d;

          if (dateFrom && dateTo && dateFrom instanceof Date && dateTo instanceof Date) {
            this.internalDateFrom = dateFrom;
            this.internalDateTo = dateTo;
            this.$emit("date-changed", { dateFrom, dateTo });
          }
        }

        this.lastClickInsidePicker = false;
      },

      onDocumentClick(event) {
        try {
          const target = event.target;
          const clickedInsidePanel = typeof target?.closest === "function" && !!target.closest(".mdrp__panel");

          if ((this.$el && this.$el.contains(target)) || clickedInsidePanel) {
            this.lastClickInsidePicker = true;
            return;
          }

          this.lastClickInsidePicker = false;
        } catch (err) {
          this.lastClickInsidePicker = false;
        }
      },
      formatDate(date) {
        if (!date) return "";

        const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

        const day = date.getDate();
        const month = months[date.getMonth()];

        return `${day} de ${month}`;
      },
      formatDay(date) {
        if (!date) return "";
        return date.getDate();
      },
      formatMonth(date) {
        if (!date) return "";
        const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
        return months[date.getMonth()];
      },
      clearDates() {
        this.internalDateFrom = null;
        this.internalDateTo = null;
        if (this.$refs.datePicker) {
          this.$nextTick(() => {
            if (this.$refs.datePicker.startDate) {
              this.$refs.datePicker.startDate = null;
            }
            if (this.$refs.datePicker.endDate) {
              this.$refs.datePicker.endDate = null;
            }
          });
        }
      },
    },
    mounted() {
      this.disablePickerOutsideClose();
      document.addEventListener("click", this.onDocumentClick, true);
    },
    beforeDestroy() {
      document.removeEventListener("click", this.onDocumentClick, true);
      this.restorePickerOutsideClose();
    },
  };
</script>

<template>
  <div class="molecule-date-picker">
    <v-md-date-range-picker ref="datePicker" @change="handleDateChange" opens="center"></v-md-date-range-picker>

    <div class="molecule-date-picker__display" @click="openPicker" v-if="internalDateFrom">
      <div class="molecule-date-picker__textContainer">
        <iconic name="calendarIcon" class="molecule-date-picker__icon" />
        <span class="molecule-date-picker__text">
          <span class="molecule-date-picker__day">{{ formatDay(internalDateFrom) }}</span>
          <span class="molecule-date-picker__month"> de {{ formatMonth(internalDateFrom) }}</span>
          <span> - </span>
          <span class="molecule-date-picker__day">{{ formatDay(internalDateTo) }}</span>
          <span class="molecule-date-picker__month"> de {{ formatMonth(internalDateTo) }}</span>
        </span>
      </div>
      <iconic name="editPencil" class="molecule-date-picker__icon" />
    </div>
  </div>
</template>

<style lang="scss">
  .molecule-date-picker {
    @include Flex(column);
    &__title {
      margin: 0 0 12px 0;
      font-size: 16px;
      font-weight: 500;
      font-family: $newFont_medium;
      text-transform: capitalize;
      color: #3d3d3d;
    }
    &__textContainer {
      @include Flex(row, flex-start, center);
      gap: 8px;
    }
    &__display {
      @include Flex(row, space-between, center);
      width: 100%;
      gap: 8px;
      padding: 10px 16px;
      background: #f8f8f8;
      border-radius: 7px;
      cursor: pointer;
      font-size: 16px;
      color: #666;
      transition: all 0.2s;

      &:hover {
        background: #e9ecef;
        border-color: #adb5bd;
      }
    }

    &__icon {
      font-size: 16px;
      color: #000000;
    }

    &__text {
      color: #333;
      font-weight: 500;
    }

    &__day {
      font-family: $newFont_semiBold;
      font-weight: 600;
    }

    &__month {
      font-family: $newFont_regular;
      color: #333;
      font-weight: 400;
      margin-right: 4px;
      text-transform: lowercase;
    }

    .mdrp__activator {
      display: none;
    }

    .mdrp__panel.dropdown-menu[data-v-7863e830] {
      position: relative;
      margin-top: -30px;
      left: 0px;
      padding: $mpadding;
      margin-bottom: 45px;
    }

    .mdrp__panel .calendar-table.right[data-v-7863e830] {
      display: none;
    }

    table.calendar-table td.calendar-cell.active .calendar-cell__content[data-v-89ba891e],
    table.calendar-table td.calendar-cell.active:hover .calendar-cell__content[data-v-89ba891e] {
      background-color: $lipstick;
    }

    .ranges ul[data-v-4e185066] .active {
      color: $lipstick;
    }
  }
</style>
